import { supabase, run } from './db';
import { cached } from './local/cache';

/**
 * Faturas e parcelas.
 *
 * A fatura passou a ser a verdade do financeiro; `services.billed/paid` são
 * reflexo mantido por gatilho, para as telas antigas continuarem funcionando
 * enquanto migram.
 *
 * O valor da fatura NÃO é campo: é a soma das parcelas, e vem pronta da view
 * `invoices_com_total`. Guardar o total ao lado das parcelas abriria espaço
 * para os dois discordarem — em dinheiro, é o pior tipo de divergência.
 */

/** No máximo 4, e o banco recusa mais que isso. */
export const MAX_PARCELAS = 4;

export const listInvoices = (laboratoryId) =>
  cached(`invoices:${laboratoryId}`, () => run(supabase
    .from('invoices_com_total')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('emitida_em', { ascending: false })));

export const listInstallments = (invoiceIds) =>
  run(supabase
    .from('invoice_installments')
    .select('*')
    .in('invoice_id', invoiceIds)
    .order('numero'));

/**
 * Divide um valor em N parcelas que somam exatamente o total.
 *
 * A sobra dos centavos vai para a PRIMEIRA parcela, não distribuída: 100/3 são
 * duas de 33,33 e uma de 33,34, e é preciso que alguém fique com o centavo a
 * mais. Dividir sem isso gera fatura que não fecha com o total, e a diferença
 * só aparece na conciliação.
 */
export const dividirEmParcelas = (total, quantidade) => {
  const centavos = Math.round(Number(total) * 100);
  const base = Math.floor(centavos / quantidade);
  const sobra = centavos - base * quantidade;

  return Array.from({ length: quantidade }, (_, i) => ({
    numero: i + 1,
    valor: (base + (i === 0 ? sobra : 0)) / 100,
  }));
};

/** Vencimentos mensais a partir de uma data, um por parcela. */
export const vencimentosMensais = (primeiroVencimento, quantidade) => {
  if (!primeiroVencimento) return Array(quantidade).fill(null);
  const [y, m, d] = String(primeiroVencimento).slice(0, 10).split('-').map(Number);

  return Array.from({ length: quantidade }, (_, i) => {
    // Dia 31 em mês de 30 cai no último dia daquele mês, e não vira dia 1 do
    // seguinte: `Date.UTC(y, m + 1, 0)` dá o último dia do mês alvo.
    const ultimoDia = new Date(Date.UTC(y, m + i, 0)).getUTCDate();
    const t = new Date(Date.UTC(y, m - 1 + i, Math.min(d, ultimoDia)));
    return t.toISOString().slice(0, 10);
  });
};

/**
 * Emite a fatura com as parcelas.
 *
 * Não é enfileirável: emitir cobrança é ato que precisa de número do servidor
 * e não pode acontecer duas vezes. Sem conexão, o certo é avisar — não guardar
 * para depois e arriscar duplicar a cobrança do cliente.
 */
export const criarFatura = async ({ laboratoryId, clientId, serviceId, tipo = 'servico',
                                    emitidaEm, observacoes, parcelas,
                                    formaPagamento, formaPagamentoObs }) => {
  const fatura = await run(supabase.from('invoices').insert([{
    laboratory_id: laboratoryId,
    client_id: clientId || null,
    service_id: serviceId || null,
    tipo,
    emitida_em: emitidaEm || new Date().toISOString().slice(0, 10),
    observacoes: observacoes || null,
    // Vazio grava NULO: '' violaria o check e, pior, pareceria preenchido.
    forma_pagamento:     formaPagamento || null,
    forma_pagamento_obs: formaPagamentoObs?.trim() || null,
  }]).select().single());

  await run(supabase.from('invoice_installments').insert(
    parcelas.map(p => ({
      invoice_id: fatura.id,
      numero: p.numero,
      valor: p.valor,
      vencimento: p.vencimento || null,
    }))
  ));

  return fatura;
};

export const pagarParcela = (id, quando = new Date().toISOString()) =>
  run(supabase.from('invoice_installments').update({ paga_em: quando }).eq('id', id));

export const desfazerPagamento = (id) =>
  run(supabase.from('invoice_installments').update({ paga_em: null }).eq('id', id));

/**
 * Muda o vencimento de UMA parcela.
 *
 * Só a data: valor e número continuam sendo o documento emitido. Vencimento
 * vazio é recusado aqui porque parcela sem data sai de toda cobrança — o
 * painel avisa disso, e editar não pode ser a porta de entrada do problema.
 * A trilha de auditoria guarda a data anterior pelo gatilho da tabela.
 */
export const alterarVencimento = (id, vencimento) => {
  if (!vencimento) throw new Error('Informe a nova data de vencimento.');
  return run(supabase.from('invoice_installments')
    .update({ vencimento: String(vencimento).slice(0, 10) })
    .eq('id', id)
    .select()
    .single());
};

/**
 * Cancela em vez de apagar.
 *
 * Fatura emitida é documento: some do que se cobra, fica no que aconteceu.
 * Apagar deixaria um buraco na numeração sem explicação.
 */
export const cancelarFatura = (id) =>
  run(supabase.from('invoices').update({ cancelada_em: new Date().toISOString() }).eq('id', id));

/**
 * Faturas de SERVIÇO de um serviço — inclusive as canceladas.
 *
 * `tipo = 'servico'` de propósito: a de `repasse` é a entrada por embrião, que
 * tem vida própria e não sai junto no refaturamento — quem pagou a entrada
 * pagou, e o encontro de contas continua valendo.
 *
 * As canceladas entram na lista porque o app cancelou em vez de apagar durante
 * um tempo, e quem refaturou naquele período tem documentos pendurados que
 * hoje não teriam sido criados. Refaturar limpa os dois.
 */
export const faturasDoServico = (serviceId) =>
  run(supabase
    .from('invoices')
    .select('id, numero_exibicao, cancelada_em')
    .eq('service_id', serviceId)
    .eq('tipo', 'servico'));

/**
 * Refaturar = APAGAR o que foi emitido e devolver o serviço para "A Faturar".
 *
 * Apaga, e não cancela. A versão anterior cancelava, com o argumento de que
 * fatura emitida é documento — o argumento não se sustenta aqui:
 *
 *   1. `audit_log` tem gatilho de DELETE em `invoices`. A linha sai da tabela
 *      e PERMANECE na trilha, com quem apagou e quando. O registro do que
 *      aconteceu não se perde; o que sai é o documento em si.
 *
 *   2. Refaturar acontece porque o valor saiu ERRADO. Guardar quatro faturas
 *      canceladas do mesmo serviço não documenta nada — polui a consulta e faz
 *      a pessoa procurar qual das cinco vale.
 *
 * Não é preciso mexer em `services`: `recalcula_espelho_do_servico` roda no
 * gatilho de DELETE e devolve `billed` e `billed_value` sozinho.
 *
 * `invoice_installments` tem ON DELETE CASCADE, então as parcelas saem junto.
 */
export const refaturar = async (serviceId) => {
  const faturas = await faturasDoServico(serviceId) || [];
  if (faturas.length === 0) return [];

  const ids = faturas.map(f => f.id);

  /**
   * A trava mora AQUI, e não só na tela: apagar um documento contra o qual
   * entrou dinheiro deixa o caixa apontando para o nada.
   *
   * O QUE ELA OLHA É O RECEBIMENTO, NÃO A QUITAÇÃO. Olhava `paga_em`, e
   * `paga_em` só é carimbada quando o recebido cobre a parcela INTEIRA — é
   * consequência da soma dos recebimentos, não um clique. Uma parcela de
   * R$ 10.000 com R$ 4.000 já recebidos tinha `paga_em` nulo, passava pela
   * trava, e o `delete` levava a fatura, as parcelas em cascata e os
   * recebimentos em cascata depois delas. Os R$ 4.000 sumiam do sistema em
   * silêncio — e `recebimentos` não tem gatilho de auditoria, então sumiam
   * sem deixar rastro em lugar nenhum.
   */
  const parcelas = await run(supabase
    .from('invoice_installments')
    .select('id')
    .in('invoice_id', ids));

  const idsDasParcelas = (parcelas || []).map(p => p.id);
  const recebidos = idsDasParcelas.length === 0 ? [] : await run(supabase
    .from('recebimentos')
    .select('id, valor')
    .in('installment_id', idsDasParcelas));

  if ((recebidos || []).length > 0) {
    const total = recebidos.reduce((s, r) => s + (Number(r.valor) || 0), 0);
    throw new Error(
      `Há ${recebidos.length} recebimento(s), somando R$ ${total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}, `
      + 'lançado(s) contra as parcelas deste serviço. Apague os lançamentos antes de refaturar — '
      + 'senão o dinheiro recebido some junto com a fatura.');
  }

  await run(supabase.from('invoices').delete().in('id', ids));
  // Só as que valiam algo aparecem no aviso: dizer que apagou quatro
  // canceladas que o próprio app criou sem querer só confunde.
  return faturas.filter(f => !f.cancelada_em).map(f => f.numero_exibicao).filter(Boolean);
};

/**
 * Refaturar a ENTRADA: apaga a fatura de entrada e deixa a entrada para ser
 * emitida de novo, com a quantidade de hoje.
 *
 * Não existia. O refaturar só alcançava a fatura do serviço, e no laboratório
 * quase todo serviço em andamento tem só a entrada emitida — a opção nem
 * aparecia. O caso que pediu (29/10/2026): o 2026-0087 tem entrada por 19
 * embriões e TE com 21, e um descongelamento lançado errado numa TE deixa a
 * entrada cobrando embrião que voltou para o botijão.
 *
 * `entrada_embrioes` fica congelada enquanto há entrada emitida (a TE não
 * regrava). Apagada a fatura, ela volta a ser a quantidade transferida, para
 * a nova emissão sair com o número certo sem precisar reabrir a TE.
 *
 * Mesma trava do refaturar do serviço: recebimento lançado bloqueia.
 */
export const refaturarEntrada = async (service) => {
  const faturas = await run(supabase
    .from('invoices')
    .select('id, numero_exibicao, cancelada_em')
    .eq('service_id', service.id)
    .eq('tipo', 'repasse')) || [];
  if (faturas.length === 0) return [];

  const ids = faturas.map(f => f.id);
  const parcelas = await run(supabase.from('invoice_installments').select('id').in('invoice_id', ids));
  const idsDasParcelas = (parcelas || []).map(p => p.id);
  const recebidos = idsDasParcelas.length === 0 ? [] : await run(supabase
    .from('recebimentos')
    .select('id, valor')
    .in('installment_id', idsDasParcelas));

  if ((recebidos || []).length > 0) {
    const total = recebidos.reduce((s, r) => s + (Number(r.valor) || 0), 0);
    throw new Error(
      `Há ${recebidos.length} recebimento(s), somando R$ ${total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}, `
      + 'lançado(s) contra a entrada deste serviço. Apague os lançamentos antes de refaturar — '
      + 'senão o dinheiro recebido some junto com a fatura.');
  }

  await run(supabase.from('invoices').delete().in('id', ids));
  await run(supabase.from('services')
    .update({ entrada_embrioes: Number(service.te_quantity) || 0 })
    .eq('id', service.id));
  return faturas.filter(f => !f.cancelada_em).map(f => f.numero_exibicao).filter(Boolean);
};

/**
 * A FATURA DO SERVIÇO SUPERA A DA ENTRADA.
 *
 * A entrada é adiantamento: ela cobra, antes da hora, parte do mesmo serviço.
 * Quando o acerto final sai, ele cobra o preço cheio — e aí a cobrança
 * provisória não pode continuar de pé, senão os mesmos embriões ficam
 * cobrados em dois documentos e o cliente não sabe qual pagar.
 *
 * Duas coisas acontecem, nesta ordem:
 *
 *   1. O DINHEIRO SEGUE A COBRANÇA. Os recebimentos lançados contra a entrada
 *      passam para a primeira parcela da fatura nova. O caixa não muda — o
 *      cliente pagou uma vez só —, mas o crédito passa a abater onde a dívida
 *      agora mora. É assim que "fatura cheia de R$ 1.500 com R$ 950 de
 *      entrada paga" vira saldo de R$ 550 em todo relatório, sem nenhum
 *      relatório precisar conhecer a regra.
 *
 *   2. A ENTRADA É CANCELADA, não apagada. Ela foi um documento emitido e
 *      continua na trilha; o que muda é que deixa de ser cobrança viva.
 *
 * Sem serviço, sem entrada viva ou sem parcela na fatura nova, não faz nada e
 * devolve `null`. Faturar um serviço que nunca teve entrada é o caso comum.
 */
export const entradaSuperadaPelaFatura = async (serviceId, faturaNovaId) => {
  if (!serviceId || !faturaNovaId) return null;

  const entradas = await run(supabase
    .from('invoices')
    .select('id, numero_exibicao')
    .eq('service_id', serviceId)
    .eq('tipo', 'repasse')
    .is('cancelada_em', null));

  if (!entradas || entradas.length === 0) return null;

  const primeira = await run(supabase
    .from('invoice_installments')
    .select('id')
    .eq('invoice_id', faturaNovaId)
    .order('numero')
    .limit(1));

  const destino = primeira?.[0]?.id;
  if (!destino) return null;

  const idsDaEntrada = entradas.map(f => f.id);
  const parcelas = await run(supabase
    .from('invoice_installments')
    .select('id')
    .in('invoice_id', idsDaEntrada));

  // O dinheiro primeiro. Cancelar antes deixaria os recebimentos pendurados
  // numa fatura cancelada, onde nenhuma conta os enxerga.
  let movidos = 0;
  const idsDasParcelas = (parcelas || []).map(p => p.id);
  if (idsDasParcelas.length > 0) {
    const movidas = await run(supabase
      .from('recebimentos')
      .update({ installment_id: destino })
      .in('installment_id', idsDasParcelas)
      .select('id'));
    movidos = (movidas || []).length;
  }

  await run(supabase
    .from('invoices')
    .update({ cancelada_em: new Date().toISOString() })
    .in('id', idsDaEntrada));

  return { canceladas: entradas.map(f => f.numero_exibicao).filter(Boolean), movidos };
};
